/**
 * The overlay kit's behaviour, shared by Dialog, Drawer, Popover and Menu in
 * this folder: Escape, outside click, the focus trap, and focus return.
 *
 * Why one engine: the rules pages hand-rolled a modal per use, each with its
 * own Escape listener (the eslint `[esc]` bucket counts them), and none of
 * them kept Tab inside or put focus back, so a keyboard user who closed a
 * confirmation landed on <body> at the top of the page. This folder is the
 * one place allowed to compare a key with 'Escape' (eslint.config.js exempts
 * it from `[esc]`).
 *
 * The DOM work lives in `attachOverlay`, a plain function over small
 * structural types, so overlay.test.tsx can drive open → Tab → Escape → close
 * with fake nodes (the unit runner has no DOM). `useOverlay` is the React
 * glue: it attaches while `open` and detaches on close, on unmount, and when
 * a keep-alive tab pane hides (TabsHost unmounts a hidden pane's effects).
 *
 * Layers: overlays stack (a menu inside a drawer, a popover inside a dialog).
 * Only the topmost layer reacts to Escape, outside clicks and Tab, so one
 * Escape closes one layer, and a click inside a child popover, which is
 * portaled outside the dialog's DOM, never reads as "outside" the dialog.
 */
import { useEffect, useEffectEvent, useId, useLayoutEffect, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

/* ── Structural types (a real element / document satisfies them) ─────────── */

type Focusable = { focus(options?: FocusOptions): void }

/** What the engine needs from an overlay's panel element. */
export type OverlayPanel = Focusable & {
  contains(other: unknown): boolean
  querySelectorAll(selectors: string): ArrayLike<unknown>
}

type OverlayEvent = 'keydown' | 'mousedown' | 'focusin'

/** What the engine needs from the document. */
export type OverlayHost = {
  readonly activeElement: unknown
  readonly body: unknown
  addEventListener(type: OverlayEvent, listener: (event: Event) => void, capture: boolean): void
  removeEventListener(type: OverlayEvent, listener: (event: Event) => void, capture: boolean): void
}

function isFocusable(node: unknown): node is Focusable {
  return typeof node === 'object' && node !== null && typeof (node as Focusable).focus === 'function'
}

/* ── Tab stops and the trap ──────────────────────────────────────────────── */

/** Everything that can take focus; `tabStops` then drops tabindex=-1 and hidden nodes. */
const FOCUSABLE_SELECTOR = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'summary', 'iframe',
  '[contenteditable]:not([contenteditable="false"])', '[tabindex]',
].join(', ')

/** The panel's Tab stops in DOM order. */
export function tabStops(panel: OverlayPanel): Focusable[] {
  const nodes = panel.querySelectorAll(FOCUSABLE_SELECTOR)
  const out: Focusable[] = []
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i] as Focusable & { tabIndex?: number; getClientRects?: () => { length: number } }
    if (!isFocusable(n)) continue
    if (typeof n.tabIndex === 'number' && n.tabIndex < 0) continue
    // display:none (or detached) has no boxes and cannot take focus; the trap
    // must not try to wrap onto it.
    if (typeof n.getClientRects === 'function' && n.getClientRects().length === 0) continue
    out.push(n)
  }
  return out
}

/**
 * Where Tab (Shift+Tab when `shift`) must send focus so it stays inside the
 * panel, or null when the browser's own next stop is still inside.
 */
export function trapTarget(
  stops: readonly Focusable[], active: unknown, shift: boolean, panel: Focusable,
): Focusable | null {
  if (stops.length === 0) return panel
  const at = stops.indexOf(active as Focusable)
  // Focus is on the panel itself (where it lands on open) or somewhere that
  // is not a stop: enter at the near edge.
  if (at === -1) return shift ? stops[stops.length - 1] : stops[0]
  if (shift && at === 0) return stops[stops.length - 1]
  if (!shift && at === stops.length - 1) return stops[0]
  return null
}

/* ── Layers ──────────────────────────────────────────────────────────────── */

const layers: string[] = []

export function isTopLayer(id: string): boolean {
  return layers.length > 0 && layers[layers.length - 1] === id
}

/** The open layers, bottom first (tests only). */
export function openLayers(): readonly string[] {
  return layers
}

/* ── Focus return ────────────────────────────────────────────────────────── */

/**
 * Put focus back on the element that had it when the overlay opened, unless
 * the person already moved it somewhere else on purpose (clicked another
 * control, which closed a popover on the way). Returns whether it moved focus.
 */
export function returnFocus(host: OverlayHost, opener: unknown, panel: OverlayPanel): boolean {
  const active = host.activeElement
  const stranded = active == null || active === host.body || panel.contains(active)
  if (!stranded || opener === host.body || !isFocusable(opener)) return false
  // A row that re-rendered away while the dialog was open took its button with it.
  if ((opener as { isConnected?: boolean }).isConnected === false) return false
  opener.focus({ preventScroll: true })
  return true
}

/* ── The engine ──────────────────────────────────────────────────────────── */

export type AttachOptions = {
  panel: OverlayPanel
  /** Clicks here are not "outside" (the button that opened a popover or menu). */
  anchor?: { contains(other: unknown): boolean } | null
  /** Where focus goes on open; the panel itself when null. */
  initialFocus?: Focusable | null
  /**
   * Dialog, Drawer: the page behind is off limits. Focus that lands there is
   * pulled back, and a mousedown on the scrim never moves focus.
   */
  modal: boolean
  /** Keep Tab and Shift+Tab inside the panel. */
  trapFocus: boolean
  /** Read at event time: close on a mousedown outside panel and anchor. */
  closesOnOutsideClick: () => boolean
  /** Read at event time: false while a request is in flight, so Escape and outside clicks do nothing. */
  canDismiss: () => boolean
  onClose: () => void
  /** A layer's own keys (Menu's arrows), run before the trap; true when it handled the key. */
  onKey?: (event: KeyboardEvent) => boolean
}

/** Opt-out marker: a click inside an element carrying this attribute is never an "outside" click for any layer
 *  (the Pending changes toast floats beside the drawer; dismissing it must not close the drawer behind it). */
export const OVERLAY_IGNORE_ATTR = 'data-overlay-ignore'

function inIgnoredRegion(target: unknown): boolean {
  const el = target as { closest?: (selector: string) => unknown } | null
  return !!el && typeof el.closest === 'function' && !!el.closest(`[${OVERLAY_IGNORE_ATTR}]`)
}

/**
 * Wire one open overlay into the document. Returns the detach function, which
 * removes the listeners, pops the layer, and returns focus to the opener.
 */
export function attachOverlay(host: OverlayHost, id: string, o: AttachOptions): () => void {
  // Captured before focus moves in: this is where focus goes back to.
  const opener = host.activeElement
  // Push BEFORE moving focus: a parent modal's focusin guard must already see
  // this layer on top, or it would pull focus straight back out of it.
  layers.push(id)
  ;(o.initialFocus ?? o.panel).focus({ preventScroll: true })

  const inside = (target: unknown) =>
    o.panel.contains(target) || !!o.anchor?.contains(target) || inIgnoredRegion(target)

  const onKeyDown = (event: Event) => {
    const e = event as KeyboardEvent
    // A key something inside already used says so with preventDefault: an
    // input clearing itself on Escape, an autocomplete taking Tab. That also
    // covers the key that OPENED this layer (ArrowDown on a menu button): its
    // handler runs at the React root, the layer attaches synchronously, and
    // the same event then reaches this listener on the document.
    if (!isTopLayer(id) || e.defaultPrevented) return
    if (e.key === 'Escape') {
      if (!o.canDismiss()) return
      e.preventDefault()
      o.onClose()
      return
    }
    if (o.onKey?.(e)) return
    if (e.key === 'Tab' && o.trapFocus) {
      const to = trapTarget(tabStops(o.panel), host.activeElement, e.shiftKey, o.panel)
      if (to) {
        e.preventDefault()
        to.focus()
      }
    }
  }

  const onMouseDown = (event: Event) => {
    if (!isTopLayer(id) || inside(event.target)) return
    // A modal's "outside" is its own scrim. Left alone, the mousedown's
    // default action focuses <body> AFTER the close has put focus back on the
    // opener (and while not dismissible it would drop focus out of the trap).
    // A non-modal's outside is a real control, which should get the focus.
    if (o.modal) event.preventDefault()
    if (!o.closesOnOutsideClick() || !o.canDismiss()) return
    o.onClose()
  }

  const onFocusIn = (event: Event) => {
    if (!o.modal || !isTopLayer(id) || inside(event.target)) return
    ;(tabStops(o.panel)[0] ?? o.panel).focus({ preventScroll: true })
  }

  // keydown bubbles (so an inner handler can claim Escape first); mousedown
  // and focusin capture (so nothing inside the page can swallow them).
  host.addEventListener('keydown', onKeyDown, false)
  host.addEventListener('mousedown', onMouseDown, true)
  host.addEventListener('focusin', onFocusIn, true)

  return () => {
    host.removeEventListener('keydown', onKeyDown, false)
    host.removeEventListener('mousedown', onMouseDown, true)
    host.removeEventListener('focusin', onFocusIn, true)
    const at = layers.lastIndexOf(id)
    if (at !== -1) layers.splice(at, 1)
    returnFocus(host, opener, o.panel)
  }
}

/* ── React glue ──────────────────────────────────────────────────────────── */

export type UseOverlayOptions = {
  onClose: () => void
  /** The trigger of a popover or menu: clicking it is not an outside click. */
  anchorRef?: RefObject<HTMLElement | null>
  /** Where focus goes on open: an element, or a function of the panel. The panel itself when absent. */
  initialFocus?: RefObject<HTMLElement | null> | ((panel: HTMLElement) => HTMLElement | null | undefined)
  /** Dialog and Drawer: the page behind is off limits (see AttachOptions.modal). Default false. */
  modal?: boolean
  /** Keep Tab inside. Default true. */
  trapFocus?: boolean
  /** Default true. */
  closeOnOutsideClick?: boolean
  /** false while a request is in flight: Escape and outside clicks do nothing. Default true. */
  dismissible?: boolean
  onKey?: (event: KeyboardEvent) => boolean
}

/**
 * Attach the overlay behaviour while `open`. Callbacks and `dismissible` are
 * read when an event fires, not when the overlay opened, so a parent that
 * passes a fresh arrow each render (or flips `dismissible` while saving) never
 * re-attaches, which would bounce focus to the opener and back.
 */
export function useOverlay(open: boolean, panelRef: RefObject<HTMLElement | null>, opts: UseOverlayOptions): void {
  const id = useId()
  const { anchorRef, modal = false, trapFocus = true } = opts
  const onClose = useEffectEvent(() => opts.onClose())
  const canDismiss = useEffectEvent(() => opts.dismissible !== false)
  const closesOnOutsideClick = useEffectEvent(() => opts.closeOnOutsideClick !== false)
  const onKey = useEffectEvent((e: KeyboardEvent) => opts.onKey?.(e) ?? false)
  const initialFocus = useEffectEvent((panel: HTMLElement): HTMLElement | null => {
    const f = opts.initialFocus
    if (!f) return null
    return (typeof f === 'function' ? f(panel) : f.current) ?? null
  })

  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    const panel = panelRef.current
    if (!panel) return
    return attachOverlay(document, id, {
      panel,
      anchor: anchorRef?.current ?? null,
      initialFocus: initialFocus(panel),
      modal,
      trapFocus,
      closesOnOutsideClick: () => closesOnOutsideClick(),
      canDismiss: () => canDismiss(),
      onClose: () => onClose(),
      onKey: e => onKey(e),
    })
  }, [open, id, panelRef, anchorRef, modal, trapFocus])
}

/* ── Anchored placement (Popover, Menu) ──────────────────────────────────── */

export type AnchorRect = { top: number; bottom: number; left: number; right: number }
export type Align = 'start' | 'end'

/**
 * Where a fixed-position panel goes next to its anchor: below it, or above
 * when it does not fit below and does fit above; aligned to the anchor's
 * left ('start') or right ('end') edge; kept `margin` px inside the viewport.
 */
export function anchoredPosition(
  anchor: AnchorRect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  align: Align,
  gap = 4,
  margin = 8,
): { top: number; left: number; side: 'below' | 'above' } {
  const below = anchor.bottom + gap
  const above = anchor.top - gap - panel.height
  const fitsBelow = below + panel.height <= viewport.height - margin
  const side = !fitsBelow && above >= margin ? 'above' : 'below'
  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi))
  return {
    top: clamp(side === 'below' ? below : above, margin, viewport.height - margin - panel.height),
    left: clamp(align === 'end' ? anchor.right - panel.width : anchor.left, margin, viewport.width - margin - panel.width),
    side,
  }
}

/**
 * Place a portaled `fixed` panel next to its anchor before the first paint,
 * and dismiss it on a scroll outside the panel or a resize: a fixed panel
 * cannot follow a scrolling container, so it closes rather than float away
 * from its row (the same rule as ops/lib/useAnchoredMenu). A scroll INSIDE the
 * panel (a long menu) does not close it.
 */
export function useAnchoredPanel(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  align: Align,
  onDismiss: () => void,
): void {
  const dismiss = useEffectEvent(() => onDismiss())
  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') return
    const anchor = anchorRef.current
    const panel = panelRef.current
    if (!anchor || !panel) return
    const p = panel.getBoundingClientRect()
    const pos = anchoredPosition(
      anchor.getBoundingClientRect(),
      { width: p.width, height: p.height },
      { width: window.innerWidth, height: window.innerHeight },
      align,
    )
    panel.style.top = `${pos.top}px`
    panel.style.left = `${pos.left}px`
    const onScroll = (e: Event) => { if (!panel.contains(e.target as Node)) dismiss() }
    const onResize = () => dismiss()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, anchorRef, panelRef, align])
}

/* ── Menu keys ───────────────────────────────────────────────────────────── */

/**
 * The item a menu key moves to (ArrowDown/ArrowUp wrap, Home, End), or null
 * for any other key. `current` is -1 when no item has focus. Disabled items
 * stay reachable, as the ARIA menu pattern asks, so their reason can be read.
 */
export function nextMenuIndex(current: number, count: number, key: string): number | null {
  if (count <= 0) return null
  switch (key) {
    case 'ArrowDown': return current < 0 || current >= count - 1 ? 0 : current + 1
    case 'ArrowUp': return current <= 0 ? count - 1 : current - 1
    case 'Home': return 0
    case 'End': return count - 1
    default: return null
  }
}

/* ── Rendering helpers ───────────────────────────────────────────────────── */

/**
 * Render into document.body so no clipping ancestor (a table cell's
 * overflow:hidden, a transformed parent) can cut the overlay off. Inline when
 * there is no document: the unit runner renders to a string. A portal inside
 * a hidden keep-alive pane is hidden with it (React's Activity hides portaled
 * host nodes too).
 */
export function toBody(node: ReactNode): ReactNode {
  return typeof document === 'undefined' ? node : createPortal(node, document.body)
}

/**
 * The dimming layer behind Dialog and Drawer. There is no scrim token in
 * src/index.css; black at 30% is what every existing modal uses (ops Drawer,
 * Rules, Approvals) and reads the same on both themes.
 */
export const SCRIM_CLASS = 'fixed inset-0 bg-black/30'

/**
 * React bubbles synthetic events through portals to the component's React
 * parents, so a click inside a dialog opened from a clickable row would also
 * "click the row". Overlay roots stop the click there.
 */
export function stopClickBubbling(e: { stopPropagation(): void }): void {
  e.stopPropagation()
}
